import React, { useEffect, useMemo, useState } from 'react';
import '../styles/CalendarioPage.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalendarCheck,
    faClock,
    faPen,
    faPlus,
    faTrashCan,
} from '@fortawesome/free-solid-svg-icons';
import { collection, deleteDoc, doc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../firebase/firebaseConfig';
import Calendario from '../components/Calendario/Calendario';
import bgShapes from '../assets/BG-shapes-1.png';
import { useFormContext } from '../context/FormContext';

const toDateKey = (date) => (
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
);

const parseDateKey = (dateKey) => {
    const [year, month, day] = String(dateKey || '').split('-').map(Number);
    return year && month && day ? new Date(year, month - 1, day) : null;
};

const formatEventDate = (dateKey) => {
    const date = parseDateKey(dateKey);
    if (!date) return 'Data não informada';

    return new Intl.DateTimeFormat('pt-BR', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
    }).format(date);
};

const CalendarioPage = () => {
    const { setFormAberto, setSelectedDate, setEventoSelecionado } = useFormContext();
    const [eventos, setEventos] = useState([]);
    const userEmail = auth.currentUser?.email;

    useEffect(() => {
        if (!userEmail) return undefined;

        const eventosCollection = collection(db, `usuarios/${userEmail}/eventos`);
        return onSnapshot(
            eventosCollection,
            (eventosSnapshot) => {
                setEventos(eventosSnapshot.docs.map((eventDocument) => ({
                    id: eventDocument.id,
                    ...eventDocument.data(),
                })));
            },
            (error) => {
                console.error('Erro ao escutar eventos: ', error);
            }
        );
    }, [userEmail]);

    const today = useMemo(() => new Date(), []);
    const todayKey = toDateKey(today);
    const sevenDayLimit = useMemo(() => {
        const limit = new Date(today);
        limit.setDate(limit.getDate() + 6);
        return toDateKey(limit);
    }, [today]);

    const upcomingEvents = useMemo(() => (
        eventos
            .filter((evento) => evento.data >= todayKey)
            .sort((first, second) => (
                `${first.data || ''} ${first.hora || ''}`.localeCompare(`${second.data || ''} ${second.hora || ''}`)
            ))
    ), [eventos, todayKey]);

    const todayCount = eventos.filter((evento) => evento.data === todayKey).length;
    const nextSevenDaysCount = eventos.filter((evento) => (
        evento.data >= todayKey && evento.data <= sevenDayLimit
    )).length;

    const dateLabel = new Intl.DateTimeFormat('pt-BR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
    }).format(today);

    const openNewEvent = () => {
        setSelectedDate(todayKey);
        setEventoSelecionado(null);
        setFormAberto(true);
    };

    const handleEditEvento = (evento) => {
        setEventoSelecionado(evento);
        setSelectedDate(evento.data);
        setFormAberto(true);
    };

    const handleDeleteEvento = async (evento) => {
        const confirmed = window.confirm(`Excluir o compromisso "${evento.nome}"?`);
        if (!confirmed || !userEmail) return;

        await deleteDoc(doc(db, `usuarios/${userEmail}/eventos`, evento.id));
    };

    return (
        <main className="main-container-calendario-page">
            <img className="bgshapes" src={bgShapes} alt="" aria-hidden="true" />

            <div className="calendario-workspace">
                <header className="calendario-page-header">
                    <div className="calendario-page-heading">
                        <span className="calendario-eyebrow">Agenda jurídica</span>
                        <h1>Calendário de prazos e compromissos</h1>
                        <p>{dateLabel}</p>
                    </div>

                    <div className="calendario-header-tools">
                        <div className="calendario-stats" aria-label="Resumo da agenda">
                            <div>
                                <strong>{todayCount}</strong>
                                <span>Hoje</span>
                            </div>
                            <div>
                                <strong>{nextSevenDaysCount}</strong>
                                <span>Próximos 7 dias</span>
                            </div>
                            <div>
                                <strong>{eventos.length}</strong>
                                <span>Total</span>
                            </div>
                        </div>

                        <button className="calendario-new-event" onClick={openNewEvent} type="button">
                            <FontAwesomeIcon icon={faPlus} />
                            Novo compromisso
                        </button>
                    </div>
                </header>

                <div className="calendario-layout">
                    <section className="container-calendario-page" aria-label="Calendário mensal">
                        <Calendario />
                    </section>

                    <aside className="calendario-agenda-panel">
                        <div className="calendario-agenda-header">
                            <div>
                                <span>Agenda</span>
                                <h2>Próximos compromissos</h2>
                            </div>
                            <span className="calendario-agenda-count">{upcomingEvents.length}</span>
                        </div>

                        <div className="calendario-agenda-list">
                            {upcomingEvents.length > 0 ? (
                                upcomingEvents.map((evento) => (
                                    <div className="calendario-agenda-event" key={evento.id}>
                                        <button
                                            className="calendario-event-main"
                                            onClick={() => handleEditEvento(evento)}
                                            type="button"
                                        >
                                            <span
                                                className="calendario-event-color"
                                                style={{ backgroundColor: evento.cor || '#bfdbfe' }}
                                            />
                                            <span className="calendario-event-copy">
                                                <strong>{evento.nome || 'Compromisso sem título'}</strong>
                                                <span>
                                                    <FontAwesomeIcon icon={faCalendarCheck} />
                                                    {formatEventDate(evento.data)}
                                                </span>
                                                <span>
                                                    <FontAwesomeIcon icon={faClock} />
                                                    {evento.hora || 'Horário a definir'}
                                                </span>
                                            </span>
                                        </button>

                                        <div className="calendario-event-actions">
                                            <button
                                                onClick={() => handleEditEvento(evento)}
                                                type="button"
                                                title="Editar compromisso"
                                                aria-label={`Editar ${evento.nome}`}
                                            >
                                                <FontAwesomeIcon icon={faPen} />
                                            </button>
                                            <button
                                                className="is-danger"
                                                onClick={() => handleDeleteEvento(evento)}
                                                type="button"
                                                title="Excluir compromisso"
                                                aria-label={`Excluir ${evento.nome}`}
                                            >
                                                <FontAwesomeIcon icon={faTrashCan} />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="calendario-agenda-empty">
                                    <FontAwesomeIcon icon={faCalendarCheck} />
                                    <strong>Agenda livre</strong>
                                    <p>Nenhum compromisso futuro foi cadastrado.</p>
                                    <button onClick={openNewEvent} type="button">Adicionar compromisso</button>
                                </div>
                            )}
                        </div>

                        <div className="calendario-agenda-footer">
                            <span className="calendario-footer-dot" />
                            Clique em um dia para incluir um prazo ou audiência.
                        </div>
                    </aside>
                </div>
            </div>
        </main>
    );
};

export default CalendarioPage;
